import useCredits from "features/User/hooks/useCredits";
import useAnalytics from "hooks/useAnalytics";
import { useSessionStorageState } from "hooks/useSessionStorageState";
import { useEffect, useState } from "react";

const STUDY_SNAPSHOT_SESSION_STORAGE_KEY_PREFIX = "studySnapshot";

// Helper for managing the study snapshot toggle button behavior.
// Implements logic to automatically open if the user has run the study snapshot on this paper previously,
// and fires a mixpanel event when opened manually.
function useStudySnapshotToggle(
  paperId: string,
  studySnapshotEventOverride?: () => void,
  interactionId?: string
) {
  const [isStudySnapshotOpened, setIsStudySnapshotOpened] =
    useSessionStorageState(
      `${STUDY_SNAPSHOT_SESSION_STORAGE_KEY_PREFIX}-${interactionId}-${paperId}`,
      false
    );
  const [hasBeenManuallyToggled, setHasBeenManuallyToggled] = useState(false);

  const { studySnapshotEvent } = useAnalytics();

  const { wasStudySnapshotPreviouslyRunOnPaperId } = useCredits();
  const previouslyRunPaperId = wasStudySnapshotPreviouslyRunOnPaperId(paperId);

  const toggleStudySnapshotOpened = () => {
    setIsStudySnapshotOpened(!isStudySnapshotOpened);
    setHasBeenManuallyToggled(true);
    if (!isStudySnapshotOpened && !hasBeenManuallyToggled) {
      if (studySnapshotEventOverride) {
        studySnapshotEventOverride();
      } else {
        studySnapshotEvent();
      }
    }
  };

  // Automatically open the study snapshot if it has been previously run and has not been manually toggled.
  useEffect(() => {
    if (previouslyRunPaperId && !hasBeenManuallyToggled) {
      setIsStudySnapshotOpened(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previouslyRunPaperId, hasBeenManuallyToggled]);

  return { isStudySnapshotOpened, toggleStudySnapshotOpened };
}

export default useStudySnapshotToggle;
